/**
 * 🆕 [Phase v13.60.3 — 2026-08-12] Alertes non-bloquantes de conflits de location.
 *
 * Composant affichant les conflits de location détectés au moment de la signature
 * d'un contrat. La signature n'est JAMAIS bloquée (v13.60.3) — à la place, une
 * alerte est créée que l'admin arbitre a posteriori.
 *
 * Utilisé dans :
 *  - CalendrierEquipements.jsx (page Location) : badge + carte compacte en tête.
 *  - Alertes.jsx : intégré comme type `conflit_location` dans le fetch générique.
 *
 * Rôles autorisés : proprietaire, adjoint_admin, charge_projet.
 *
 * Props :
 *  - variant: 'card' (par défaut, riche) | 'compact' (juste le badge + liste réduite).
 *  - onCountChange(n): callback quand le nombre d'alertes actives change.
 */
import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle2, XCircle, ExternalLink, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

const CONFLIT_TYPE_LABEL = {
  bon_travail: 'Bon de travail',
  route_lavage: 'Route de lavage',
  soumission_location: 'Autre contrat de location',
  location_manuelle: 'Location manuelle',
  calendrier_production: 'Calendrier de production',
};

const CONFLIT_TYPE_LINK = (type, refId) => {
  if (!refId) return null;
  switch (type) {
    case 'bon_travail':
      return `/crm/bons-travail/${refId}`;
    case 'soumission_location':
      return `/crm/soumissions/${refId}`;
    case 'route_lavage':
      return `/routes-lavage`;
    default:
      return null;
  }
};

export function AlertesConflitsLocation({ variant = 'card', onCountChange }) {
  const [alertes, setAlertes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [processingId, setProcessingId] = useState(null);

  const fetchAlertes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/alertes/conflits-location?statut=active`);
      if (res.status === 200) {
        const data = res.data;
        setAlertes(data.alertes || []);
        if (onCountChange) onCountChange((data.alertes || []).length);
      }
    } catch (e) {
      // Silencieux : rôle terrain reçoit 403, on affiche rien.
      setAlertes([]);
      if (onCountChange) onCountChange(0);
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => { fetchAlertes(); }, [fetchAlertes]);

  const updateStatut = async (alerteId, statut, note = null) => {
    setProcessingId(alerteId);
    try {
      const res = await axios.put(
        `${API_URL}/api/alertes/conflits-location/${alerteId}/statut`,
        { statut, note },
      );
      if (res.status === 200) {
        toast.success(statut === 'resolue' ? 'Alerte marquée comme résolue' : 'Alerte ignorée');
        fetchAlertes();
      }
    } catch (e) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <div className="text-xs text-slate-500 flex items-center gap-2" data-testid="alertes-conflits-location-loading">
        <Loader2 className="w-3 h-3 animate-spin" /> Chargement des alertes de conflit…
      </div>
    );
  }

  if (alertes.length === 0) {
    if (variant === 'compact') return null;
    return null; // pas d'affichage vide sur la page Location
  }

  const renderConflitLine = (conflit) => {
    const label = CONFLIT_TYPE_LABEL[conflit.type] || conflit.type || 'Source inconnue';
    const link = CONFLIT_TYPE_LINK(conflit.type, conflit.ref_id);
    return (
      <li key={`${conflit.type}-${conflit.ref_id}`} className="text-xs text-slate-700 flex items-start gap-1.5">
        <span className="text-slate-400">•</span>
        <div className="flex-1 min-w-0">
          <span className="font-medium">{label}</span>
          {conflit.label && <span className="text-slate-600"> — {conflit.label}</span>}
          {conflit.date_premier_conflit && (
            <span className="text-slate-500 ml-1">(dès le {conflit.date_premier_conflit})</span>
          )}
          {link && (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2 inline-flex items-center gap-0.5 text-blue-600 hover:underline text-xs"
              data-testid={`conflit-link-${conflit.ref_id}`}
            >
              Voir <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </li>
    );
  };

  return (
    <Card
      className="border-l-4 border-l-amber-500 bg-amber-50/40 mb-4"
      data-testid="alertes-conflits-location-card"
    >
      <CardHeader className="pb-2 pt-3">
        <CardTitle className="text-sm font-semibold text-amber-900 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          Conflits de location détectés
          <Badge
            className="bg-amber-600 text-white text-xs"
            data-testid="alertes-conflits-location-count"
          >
            {alertes.length}
          </Badge>
        </CardTitle>
        <p className="text-xs text-amber-800 mt-1">
          Signature acceptée malgré une double-réservation d&apos;équipement. Vérifiez et tranchez.
        </p>
      </CardHeader>
      <CardContent className="pt-0 pb-3 space-y-2" data-testid="alertes-conflits-location-list">
        {alertes.map((a) => {
          const isExpanded = expandedId === a.id;
          const nbConflits = (a.conflits || []).length;
          const soumissionLink = a.soumission_id ? `/crm/soumissions/${a.soumission_id}` : null;
          const dossierLink = a.dossier_id ? `/crm/dossiers/${a.dossier_id}` : null;
          return (
            <div
              key={a.id}
              className="bg-white border border-amber-200 rounded-md p-2.5"
              data-testid={`alerte-conflit-${a.id}`}
            >
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-900">
                    {a.nom_equipement}
                    {a.numero_unite_equipement && (
                      <span className="text-slate-500"> · #{a.numero_unite_equipement}</span>
                    )}
                    <span className="text-slate-500"> — {a.client_nom || 'Client'}</span>
                  </p>
                  <p className="text-xs text-slate-600 mt-0.5">
                    <span className="font-medium">Fenêtre :</span> {a.date_prise} → {a.date_remise}
                    {a.numero_contrat && <> · Contrat #{a.numero_contrat}</>}
                    <span className="ml-2 text-amber-700 font-medium">{nbConflits} conflit(s)</span>
                  </p>
                  {isExpanded && (
                    <>
                      <ul className="mt-2 space-y-1" data-testid={`alerte-conflit-details-${a.id}`}>
                        {(a.conflits || []).map(renderConflitLine)}
                      </ul>
                      {Array.isArray(a.alternatives_disponibles) && a.alternatives_disponibles.length > 0 && (
                        <div className="mt-2 text-xs text-slate-600">
                          <span className="font-medium text-emerald-700">
                            {a.alternatives_disponibles.length} alternative(s) libre(s) :
                          </span>
                          <span className="ml-1">
                            {a.alternatives_disponibles.map((alt) => alt.nom + (alt.numero_unite ? ` #${alt.numero_unite}` : '')).join(', ')}
                          </span>
                        </div>
                      )}
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {soumissionLink && (
                          <Button
                            asChild
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs"
                            data-testid={`alerte-conflit-voir-soumission-${a.id}`}
                          >
                            <a href={soumissionLink} target="_blank" rel="noopener noreferrer">
                              Voir la soumission <ExternalLink className="w-3 h-3 ml-1" />
                            </a>
                          </Button>
                        )}
                        {dossierLink && (
                          <Button
                            asChild
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs"
                            data-testid={`alerte-conflit-voir-dossier-${a.id}`}
                          >
                            <a href={dossierLink} target="_blank" rel="noopener noreferrer">
                              Voir le dossier <ExternalLink className="w-3 h-3 ml-1" />
                            </a>
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setExpandedId(isExpanded ? null : a.id)}
                    data-testid={`alerte-conflit-toggle-${a.id}`}
                  >
                    {isExpanded ? <><ChevronUp className="w-3 h-3 mr-1" /> Masquer</> : <><ChevronDown className="w-3 h-3 mr-1" /> Détails</>}
                  </Button>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                      onClick={() => updateStatut(a.id, 'resolue')}
                      disabled={processingId === a.id}
                      data-testid={`alerte-conflit-resoudre-${a.id}`}
                    >
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Résolu
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs text-slate-500 hover:text-slate-700"
                      onClick={() => updateStatut(a.id, 'ignoree')}
                      disabled={processingId === a.id}
                      data-testid={`alerte-conflit-ignorer-${a.id}`}
                    >
                      <XCircle className="w-3 h-3 mr-1" /> Ignorer
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

export default AlertesConflitsLocation;
